/**
 * CaptchaSettingsEditor
 * ─────────────────────
 * Admin settings tab for Cloudflare Turnstile (captcha).
 *
 * Lets the admin do three things without touching the server's .env file
 * or redeploying:
 *   1. Enable / disable the captcha entirely (master switch).
 *   2. Override the Turnstile Site Key (public, sent to the browser).
 *   3. Override the Turnstile Secret Key (private, used server-side for
 *      siteverify). The current value is shown masked — to keep a key
 *      that's already in the DB, the field is left empty and the
 *      "Update" request omits `secret_key` so the backend leaves it
 *      untouched.
 *
 * If the override fields are blank, the backend falls back to the env
 * vars `TURNSTILE_SITE_KEY` / `TURNSTILE_SECRET_KEY`. The "source" badge
 * next to each field tells the admin where the active value comes from
 * right now (`db` = stored in admin settings, `env` = backend .env).
 */
import React, { useEffect, useState } from 'react';
import { Shield, ShieldOff, Loader2, Eye, EyeOff, ExternalLink, AlertTriangle, CheckCircle2, Zap, XCircle, Wifi } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CaptchaSettingsEditor({ token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);     // server snapshot
  const [enabled, setEnabled] = useState(true);
  const [siteKey, setSiteKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [msg, setMsg] = useState(null);     // { type: 'ok'|'err', text }
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);   // backend response

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/config/captcha-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setCfg(data);
      setEnabled(!!data.enabled);
      setSiteKey(data.site_key_source === 'db' ? data.site_key : '');
      setSecretKey('');   // never prefill — admin must re-type to change
    } catch (e) {
      setMsg({ type: 'err', text: 'Failed to load: ' + e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);   // initial load only

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const body = { enabled, site_key: siteKey.trim() };
      // Only send `secret_key` when the admin actually typed something.
      // Sending an empty string would clear the DB override (which the
      // user can do explicitly via the "Clear stored secret" button).
      if (secretKey.trim()) body.secret_key = secretKey.trim();
      const r = await fetch(`${API_URL}/api/config/captcha-settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setCfg(data);
      setSecretKey('');
      setMsg({ type: 'ok', text: 'Saved. Changes take effect immediately on every new request.' });
    } catch (e) {
      setMsg({ type: 'err', text: 'Save failed: ' + e.message });
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await fetch(`${API_URL}/api/config/captcha-test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setTestResult(data);
    } catch (e) {
      setTestResult({ status: 'network_error', summary: 'Request failed: ' + e.message, error_codes: [] });
    } finally {
      setTesting(false);
    }
  };

  const clearSecret = async () => {
    if (!window.confirm('Clear the stored secret key? The server will fall back to the TURNSTILE_SECRET_KEY env var (if set).')) return;    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/config/captcha-settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled, site_key: siteKey.trim(), secret_key: '' }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setCfg(data);
      setMsg({ type: 'ok', text: 'Stored secret cleared. Falling back to env var.' });
    } catch (e) {
      setMsg({ type: 'err', text: 'Clear failed: ' + e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading captcha settings…
        </div>
      </div>
    );
  }

  return (
    <CaptchaSettingsView
      cfg={cfg}
      enabled={enabled}
      setEnabled={setEnabled}
      siteKey={siteKey}
      setSiteKey={setSiteKey}
      secretKey={secretKey}
      setSecretKey={setSecretKey}
      showSecret={showSecret}
      setShowSecret={setShowSecret}
      saving={saving}
      msg={msg}
      save={save}
      clearSecret={clearSecret}
      testing={testing}
      testResult={testResult}
      testConnection={testConnection}
    />
  );
}

// SourceBadge is hoisted out of CaptchaSettingsEditor to keep React from
// remounting it on every render (react/no-unstable-nested-components).
function SourceBadge({ source }) {
  return (
    <span
      className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${
        source === 'db'
          ? 'bg-cyan-100 text-cyan-700'
          : source === 'env'
            ? 'bg-violet-100 text-violet-700'
            : 'bg-slate-200 text-slate-600'
      }`}
      title={source === 'db' ? 'Stored in admin settings (this UI)' : source === 'env' ? 'Reading from backend .env file' : 'Not configured'}
    >
      {source}
    </span>
  );
}

function CaptchaSettingsView({
  cfg, enabled, setEnabled, siteKey, setSiteKey, secretKey, setSecretKey,
  showSecret, setShowSecret, saving, msg, save, clearSecret,
  testing, testResult, testConnection,
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="captcha-settings-editor">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          {enabled ? <Shield className="w-5 h-5 text-emerald-600" /> : <ShieldOff className="w-5 h-5 text-slate-400" />}
          Captcha (Cloudflare Turnstile)
        </h2>
        <a
          href="https://dash.cloudflare.com/?to=/:account/turnstile"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-1 font-semibold"
        >
          Cloudflare dashboard <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Bot protection for the registration form. Changes take effect immediately — no restart required.
      </p>

      {/* ───── Master switch ───── */}
      <div className={`rounded-lg border p-3.5 mb-4 ${enabled ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/60'}`} data-testid="captcha-enabled-card">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="mt-0.5 w-5 h-5 rounded text-cyan-600 focus:ring-cyan-500"
            data-testid="captcha-enabled-toggle"
          />
          <span className="flex-1">
            <span className={`block font-bold text-sm ${enabled ? 'text-emerald-800' : 'text-amber-800'}`}>
              {enabled ? 'Captcha is ENABLED' : 'Captcha is DISABLED'}
            </span>
            <span className="block text-[11px] text-slate-600 mt-0.5 leading-snug">
              {enabled
                ? 'New registrations must solve the Turnstile widget. Server validates each token via Cloudflare.'
                : 'Registration form will NOT show the captcha widget and the server will skip token verification. Use only as a temporary workaround — bots may register freely.'}
            </span>
          </span>
        </label>
      </div>

      {/* ───── Site Key ───── */}
      <div className="mb-4" data-testid="captcha-site-key-row">
        <div className="flex items-center gap-2 mb-1">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-700">Site Key</label>
          {cfg && <SourceBadge source={cfg.site_key_source} />}
          <span className="text-[11px] text-slate-400">— public, embedded in the page</span>
        </div>
        <input
          type="text"
          value={siteKey}
          onChange={(e) => setSiteKey(e.target.value)}
          placeholder={cfg?.site_key_source === 'env' ? `Currently using env var: ${cfg.site_key || '(empty)'}` : 'e.g. 0x4AAAAAADFng...'}
          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 outline-none"
          data-testid="captcha-site-key-input"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          Leave empty to fall back to the backend env var. The current effective value{' '}
          {cfg?.site_key ? <code className="bg-slate-100 px-1 rounded text-[10px]">{cfg.site_key}</code> : <span className="text-rose-600 font-bold">is empty</span>}.
        </p>
      </div>

      {/* ───── Secret Key ───── */}
      <div className="mb-4" data-testid="captcha-secret-key-row">
        <div className="flex items-center gap-2 mb-1">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-700">Secret Key</label>
          {cfg && <SourceBadge source={cfg.secret_key_present ? cfg.secret_key_source : 'missing'} />}
          <span className="text-[11px] text-slate-400">— private, used for server-side siteverify</span>
        </div>
        <div className="relative">
          <input
            type={showSecret ? 'text' : 'password'}
            value={secretKey}
            onChange={(e) => setSecretKey(e.target.value)}
            placeholder={
              cfg?.secret_key_present
                ? `Currently stored: ${cfg.secret_key_masked} — leave blank to keep`
                : 'Not set — paste your Cloudflare secret to enable siteverify'
            }
            className="w-full px-3 py-2 pr-9 rounded-lg border border-slate-300 text-sm font-mono focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 outline-none"
            data-testid="captcha-secret-key-input"
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowSecret((s) => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
            title={showSecret ? 'Hide' : 'Show'}
            data-testid="captcha-secret-toggle-visibility"
          >
            {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <div className="flex items-center justify-between gap-2 mt-1">
          <p className="text-[11px] text-slate-500">
            Stored encrypted at rest in the database. Get it from{' '}
            <a href="https://dash.cloudflare.com/?to=/:account/turnstile" target="_blank" rel="noopener noreferrer" className="text-cyan-700 hover:underline">
              Cloudflare → Turnstile → Edit widget
            </a>.
          </p>
          {cfg?.secret_key_source === 'db' && (
            <button
              type="button"
              onClick={clearSecret}
              disabled={saving}
              className="text-[11px] text-rose-600 hover:text-rose-800 font-semibold whitespace-nowrap"
              data-testid="captcha-secret-clear"
            >
              Clear stored secret
            </button>
          )}
        </div>
      </div>

      {/* ───── Warnings ───── */}
      {enabled && !cfg?.site_key && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800 flex items-start gap-2" data-testid="captcha-warn-no-site-key">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            <b>Captcha is enabled but no Site Key is configured.</b> The widget won&apos;t render — visitors will see &quot;Captcha failed to load&quot;. Paste a Site Key above and save.
          </span>
        </div>
      )}
      {enabled && cfg?.site_key && !cfg?.secret_key_present && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2" data-testid="captcha-warn-no-secret">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            <b>Secret Key missing.</b> The widget will render in the browser, but the backend can&apos;t validate tokens with Cloudflare → all tokens are accepted blindly. Add a Secret Key to make the protection real.
          </span>
        </div>
      )}

      {/* ───── Save / Status ───── */}
      <div className="flex items-center gap-3 pt-3 border-t border-slate-200 flex-wrap">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:shadow-md text-white text-sm font-bold disabled:opacity-50"
          data-testid="captcha-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={testConnection}
          disabled={testing}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-violet-300 bg-white hover:bg-violet-50 text-violet-700 text-sm font-bold disabled:opacity-50"
          data-testid="captcha-test-btn"
          title="Pings Cloudflare with a fake token to verify your secret key is accepted. Doesn't affect any real user."
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          {testing ? 'Testing…' : 'Test connection'}
        </button>
        {msg && (
          <p
            className={`text-xs font-medium ${msg.type === 'ok' ? 'text-emerald-700' : 'text-rose-700'}`}
            data-testid="captcha-save-msg"
          >
            {msg.text}
          </p>
        )}
      </div>

      {/* ───── Test result block — only shown after Test connection was clicked.
              Background color encodes status (green ok / red broken / amber neutral). */}
      {testResult && (
        <div
          className={`mt-3 rounded-lg border p-3 ${
            testResult.status === 'ok'
              ? 'border-emerald-200 bg-emerald-50/60'
              : testResult.status === 'network_error'
                ? 'border-amber-200 bg-amber-50/60'
                : 'border-rose-200 bg-rose-50/60'
          }`}
          data-testid="captcha-test-result"
        >
          <div className="flex items-start gap-2 mb-1">
            {testResult.status === 'ok' && <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />}
            {testResult.status === 'config_error' && <XCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />}
            {testResult.status === 'network_error' && <Wifi className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-bold ${
                testResult.status === 'ok' ? 'text-emerald-800' :
                testResult.status === 'network_error' ? 'text-amber-800' : 'text-rose-800'
              }`}>
                {testResult.status === 'ok' ? 'Connection OK' :
                 testResult.status === 'network_error' ? 'Network issue' :
                 'Configuration problem'}
              </p>
              <p className="text-xs text-slate-700 mt-0.5 leading-snug">{testResult.summary}</p>
              {(testResult.error_codes?.length || 0) > 0 && (
                <p className="text-[11px] text-slate-500 mt-1 font-mono break-all">
                  Cloudflare codes: {testResult.error_codes.join(', ')}
                </p>
              )}
              {testResult.http_status && testResult.http_status !== 200 && (
                <p className="text-[11px] text-slate-500 mt-0.5">HTTP {testResult.http_status}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
